/**
 * drlh-video-embed.js
 * Behaviour for snippets/drlh-video-embed.liquid, the branded YouTube facade.
 *
 * Extracted from that snippet's inline <script>: the snippet renders in a loop
 * on article pages, so a four-video article shipped four identical IIFEs, each
 * re-scanning every embed on the page.
 *
 * Loads the YouTube IFrame API only on the first click, then forces playback so
 * YouTube auto-hides its title/overlay quickly rather than parking on its
 * poster + title card.
 *
 * Idempotent: safe to load more than once (the snippet emits one tag per
 * instance), and per-element via data-drlh-bound.
 */
(function () {
  if (window.__drlhVideoEmbedInit) return;
  window.__drlhVideoEmbedInit = true;

  // Cap for the API poll. Without one it spun at 25Hz for the life of the page
  // when the API was blocked, and since the cover had already been wiped the
  // visitor was left with a permanently blank berry rectangle.
  var API_TIMEOUT_MS = 6000;

  function loadYT(cb) {
    if (window.YT && window.YT.Player) { cb(true); return; }
    if (!document.getElementById('drlh-yt-api')) {
      var s = document.createElement('script');
      s.id = 'drlh-yt-api';
      // The API loader itself is only served from youtube.com; the PLAYER is
      // pointed at youtube-nocookie.com via the `host` option below.
      s.src = 'https://www.youtube.com/iframe_api';
      document.head.appendChild(s);
    }
    var waited = 0;
    var poll = setInterval(function () {
      if (window.YT && window.YT.Player) { clearInterval(poll); cb(true); return; }
      waited += 40;
      if (waited >= API_TIMEOUT_MS) { clearInterval(poll); cb(false); }
    }, 40);
  }

  function restore(media, cover, label) {
    media.innerHTML = cover;
    media.classList.remove('is-playing');
    media.setAttribute('role', 'button');
    media.setAttribute('tabindex', '0');
    media.setAttribute('aria-label', label);
  }

  // Self-hosted film: a native player in the same box. No API to wait for, so
  // no spinner; the browser shows its own buffering state.
  function playFile(media, src, label) {
    media.classList.add('is-playing');
    media.removeAttribute('role');
    media.removeAttribute('tabindex');
    media.removeAttribute('aria-label');
    var video = document.createElement('video');
    video.src = src;
    video.controls = true;
    video.autoplay = true;
    video.playsInline = true;
    video.setAttribute('playsinline', '');
    video.setAttribute('preload', 'auto');
    video.setAttribute('aria-label', label);
    video.className = 'drlh-vembed__file';
    media.innerHTML = '';
    media.appendChild(video);
    var p = video.play();
    if (p && p.catch) p.catch(function () {});
    try { video.focus({ preventScroll: true }); } catch (_) {}
  }

  function play(media) {
    var id = media.getAttribute('data-video-id');
    var src = media.getAttribute('data-video-src');
    if (media.classList.contains('is-playing')) return;
    if (!id && src) { playFile(media, src, media.getAttribute('aria-label') || 'Video'); return; }
    if (!id) return;

    var cover = media.innerHTML;
    var label = media.getAttribute('aria-label') || 'Video';

    // Drop the button semantics before the iframe mounts. An iframe inside an
    // element with role=button nests interactive content inside a control, and
    // the wrapper is no longer a button once the player is there.
    media.classList.add('is-playing');
    media.removeAttribute('role');
    media.removeAttribute('tabindex');
    media.removeAttribute('aria-label');

    var holder = document.createElement('div');
    media.innerHTML = '';
    media.appendChild(holder);

    // Loading cue over the ink box, cleared once the player is ready (or on a
    // failed API load, when restore() wipes the box anyway).
    var spinner = document.createElement('span');
    spinner.className = 'drlh-vembed__spinner';
    spinner.setAttribute('aria-hidden', 'true');
    media.appendChild(spinner);
    function clearSpinner() { if (spinner && spinner.parentNode) spinner.parentNode.removeChild(spinner); }

    loadYT(function (ready) {
      if (!ready) { clearSpinner(); restore(media, cover, label); return; }
      new YT.Player(holder, {
        videoId: id,
        host: 'https://www.youtube-nocookie.com',
        playerVars: { autoplay: 1, rel: 0, modestbranding: 1, playsinline: 1, iv_load_policy: 3 },
        events: {
          onReady: function (e) {
            try { e.target.playVideo(); } catch (_) {}
            clearSpinner();
            var frame = media.querySelector('iframe');
            if (!frame) return;
            // The generated iframe has no title of its own, and focus is still
            // on a node that has just been replaced.
            frame.setAttribute('title', label);
            frame.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');
            try { frame.focus({ preventScroll: true }); } catch (_) {}
          }
        }
      });
    });
  }

  function onKeydown(ev) {
    var media = ev.currentTarget;
    if (media.classList.contains('is-playing')) return;
    if (ev.key === 'Enter' || ev.key === ' ' || ev.key === 'Spacebar') {
      // preventDefault sits AFTER the is-playing check so Space still scrolls
      // the page once the player has taken over the box.
      ev.preventDefault();
      play(media);
    }
  }

  function bind() {
    var wraps = document.querySelectorAll('[data-drlh-vembed]');
    for (var i = 0; i < wraps.length; i++) {
      var w = wraps[i];
      if (w.dataset.drlhBound || !(w.getAttribute('data-video-id') || w.getAttribute('data-video-src'))) continue;
      w.dataset.drlhBound = '1';
      // Click anywhere inside the rectangle plays.
      w.addEventListener('click', function (ev) { play(ev.currentTarget); });
      w.addEventListener('keydown', onKeydown);
    }
  }

  bind();
  // Theme editor swaps whole sections in; rebind whatever arrived with them.
  document.addEventListener('shopify:section:load', bind);
})();
